import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';
import Swal from 'sweetalert2';

function ForgotPassword() {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: Reset Password
    const [showPassword, setShowPassword] = useState(false);

    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    async function handleCheckEmail() {
        if (!email) {
            Swal.fire({ icon: 'error', title: 'Required', text: 'Please enter your email.' });
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(`${backendUrl}/api/check-email`, { email }, {
                headers: { 'Tenant-Origin': window.location.origin }
            });

            if (response.data.exists) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: 'Email verified. OTP sent to your email.',
                    showConfirmButton: false,
                    timer: 3000
                });
                setStep(2);
            }
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: 'error',
                title: 'Not Found',
                text: 'The email address entered does not exist in our records.',
            });
        } finally {
            setLoading(false);
        }
    }

    async function handleVerifyOtp() {
        if (!otp) {
            Swal.fire({ icon: 'error', title: 'Required', text: 'Please enter the OTP.' });
            return;
        }

        setLoading(true);
        try {
            // Assuming the endpoint for verifying OTP for forgot password is /api/verify-otp
            // You might need to adjust this depending on your backend
            const response = await axios.post(`${backendUrl}/api/verify-otp`, { email, otp }, {
                headers: { 'Tenant-Origin': window.location.origin }
            });

            if (response.data.status === 'success' || response.data.valid) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: 'OTP matched. You can now reset your password.',
                    showConfirmButton: false,
                    timer: 3000
                });
                setStep(3);
            } else {
                Swal.fire({ icon: 'error', title: 'Invalid OTP', text: 'The OTP entered is incorrect.' });
            }
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: 'error',
                title: 'Verification Failed',
                text: error.response?.data?.message || 'Invalid or expired OTP.',
            });
        } finally {
            setLoading(false);
        }
    }

    async function handleResetPassword() {
        if (!password || !confirmPassword) {
            Swal.fire({ icon: 'error', title: 'Required', text: 'Please fill in both password fields.' });
            return;
        }

        if (password !== confirmPassword) {
            Swal.fire({ icon: 'error', title: 'Mismatch', text: 'Passwords do not match.' });
            return;
        }

        if (password.length < 8) {
             Swal.fire({ icon: 'error', title: 'Invalid', text: 'Password must be at least 8 characters long.' });
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(`${backendUrl}/api/reset-password`, {
                email,
                otp, // Also sending OTP to ensure authenticity of the reset request
                password,
                password_confirmation: confirmPassword
            }, {
                headers: { 'Tenant-Origin': window.location.origin }
            });

            if (response.data.status === 'success') {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Your password has been reset successfully.',
                }).then(() => {
                    navigate('/login');
                });
            }
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: 'error',
                title: 'Reset Failed',
                text: error.response?.data?.message || 'An error occurred while resetting your password.',
            });
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="account-pages my-5 pt-sm-5">
            <Container>
                <Row className="justify-content-center">
                    <Col md={8} lg={6} xl={5}>
                        <Card className="overflow-hidden shadow-lg border-0">
                            <div className="bg-primary bg-soft">
                                <div className="row">
                                    <div className="col-7">
                                        <div className="text-primary p-4">
                                            <h5 className="text-primary">
                                                {step === 1 ? 'Verify Email' : step === 2 ? 'Verify OTP' : 'Reset Password'}
                                            </h5>
                                            <p>
                                                {step === 1 ? 'Check your account with V360.' : 
                                                 step === 2 ? 'Enter the code sent to your email.' : 
                                                 'Set your new password.'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-5 align-self-end">
                                        <img src="assets/images/profile-img.png" alt="" className="img-fluid" />
                                    </div>
                                </div>
                            </div>
                            <Card.Body className="pt-0">
                                <div className="auth-logo text-center mb-4">
                                    <div className="avatar-md profile-user-wid mb-4">
                                        <span className="avatar-title rounded-circle bg-light">
                                            <img src="assets/images/logo-light.svg" alt="" className="rounded-circle" height="34" />
                                        </span>
                                    </div>
                                </div>
                                <div className="p-2">
                                    {step === 1 && (
                                        <>
                                            <Alert variant="info" className="text-center mb-4">
                                                Please enter your registered email to verify your account.
                                            </Alert>
                                            <form className="form-horizontal">
                                                <div className="mb-3">
                                                    <label htmlFor="useremail" className="form-label">Email</label>
                                                    <input
                                                        type="email"
                                                        className="form-control"
                                                        id="useremail"
                                                        placeholder="Enter email"
                                                        value={email}
                                                        onChange={(e) => setEmail(e.target.value)}
                                                    />
                                                </div>
                                                <div className="text-end">
                                                    <button 
                                                        className="btn btn-primary w-md waves-effect waves-light" 
                                                        type="button"
                                                        onClick={handleCheckEmail}
                                                        disabled={loading}
                                                    >
                                                        {loading && <Spinner animation="border" size="sm" className="me-2" />}
                                                        Verify Email
                                                    </button>
                                                </div>
                                            </form>
                                        </>
                                    )}

                                    {step === 2 && (
                                        <>
                                            <Alert variant="success" className="text-center mb-4">
                                                Email verified! OTP has been sent.
                                            </Alert>
                                            <form className="form-horizontal">
                                                <div className="mb-3">
                                                    <label htmlFor="userotp" className="form-label">OTP Code</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        id="userotp"
                                                        placeholder="Enter OTP code"
                                                        value={otp}
                                                        onChange={(e) => setOtp(e.target.value)}
                                                    />
                                                </div>
                                                <div className="text-end d-flex justify-content-between">
                                                    <button 
                                                        className="btn btn-link text-muted" 
                                                        type="button"
                                                        onClick={() => setStep(1)}
                                                        disabled={loading}
                                                    >
                                                        Back
                                                    </button>
                                                    <button 
                                                        className="btn btn-primary w-md waves-effect waves-light" 
                                                        type="button"
                                                        onClick={handleVerifyOtp}
                                                        disabled={loading}
                                                    >
                                                        {loading && <Spinner animation="border" size="sm" className="me-2" />}
                                                        Verify OTP
                                                    </button>
                                                </div>
                                            </form>
                                        </>
                                    )}

                                    {step === 3 && (
                                        <>
                                            <Alert variant="success" className="text-center mb-4">
                                                OTP matched! Please enter your new password below.
                                            </Alert>
                                            <form className="form-horizontal">
                                                <div className="mb-3">
                                                    <label className="form-label">New Password</label>
                                                    <div className="input-group auth-pass-inputgroup">
                                                        <input
                                                            type={showPassword ? "text" : "password"}
                                                            className="form-control"
                                                            placeholder="Enter new password"
                                                            value={password}
                                                            onChange={(e) => setPassword(e.target.value)}
                                                        />
                                                        <button
                                                            className="btn btn-light"
                                                            type="button"
                                                            onClick={() => setShowPassword(!showPassword)}
                                                        >
                                                            <i className={`mdi ${showPassword ? "mdi-eye-off-outline" : "mdi-eye-outline"}`}></i>
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="form-label">Confirm Password</label>
                                                    <input
                                                        type={showPassword ? "text" : "password"}
                                                        className="form-control"
                                                        placeholder="Confirm new password"
                                                        value={confirmPassword}
                                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                                    />
                                                </div>
                                                <div className="text-end d-flex justify-content-between">
                                                    <button 
                                                        className="btn btn-link text-muted" 
                                                        type="button"
                                                        onClick={() => setStep(2)}
                                                        disabled={loading}
                                                    >
                                                        Back
                                                    </button>
                                                    <button 
                                                        className="btn btn-primary w-md waves-effect waves-light" 
                                                        type="button"
                                                        onClick={handleResetPassword}
                                                        disabled={loading}
                                                    >
                                                        {loading && <Spinner animation="border" size="sm" className="me-2" />}
                                                        Reset Password
                                                    </button>
                                                </div>
                                            </form>
                                        </>
                                    )}
                                </div>
                            </Card.Body>
                        </Card>
                        <div className="mt-5 text-center">
                            <p>Jump back to <Link to="/login" className="fw-medium text-primary"> Login </Link> </p>
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    );
}

export default ForgotPassword;
